import { usd, pct, ptTime } from "@/lib/api";

const GROUPS = [
  ["by_scan_window", "Win rate by scan window"],
  ["by_asset", "Win rate by asset"],
  ["by_threshold", "Win rate by velocity setting & asset"],
  ["by_multiplier", "Win rate by exit multiplier"],
  ["by_exit_reason", "By exit reason"],
  ["by_recipe", "Win rate by full recipe (window · move · multiplier)"],
];

function Breakdown({ title, rows, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-[var(--border-subtle)] p-3">
      <div className="text-[10px] uppercase tracking-widest text-[var(--text-mute)] mb-2">{title}</div>
      {rows.length === 0 ? <div className="text-xs text-[var(--text-mute)]">No closed trades yet</div> : (
        <table className="w-full text-xs font-mono-num">
          <tbody>{rows.map((b) => (
            <tr key={b.bucket} className="border-t border-[var(--border-subtle)]">
              <td className="py-1 pr-2">{b.bucket}</td><td className="pr-2 text-right">{b.trades} trades</td>
              <td className={`pr-2 text-right ${b.win_rate >= 50 ? "text-[var(--buy)]" : "text-[var(--sell)]"}`}>{pct(b.win_rate)}</td>
              <td className={`text-right ${b.net_usd >= 0 ? "text-[var(--buy)]" : "text-[var(--sell)]"}`}>{usd(b.net_usd)}</td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

export function Reports({ r }) {
  if (!r) return null;
  return (
    <section data-testid="reports" className="space-y-3">
      <div className="flex flex-wrap items-baseline gap-4">
        <h2 className="font-head font-bold text-base md:text-lg">Performance</h2>
        <span data-testid="report-total" className="font-mono-num text-sm text-[var(--text-dim)]">
          {r.total.trades} trades · {pct(r.total.win_rate)} wins · net {usd(r.total.net_usd)} after fees
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {GROUPS.map(([k, t]) => <Breakdown key={k} title={t} rows={r[k] || []} testid={`report-${k.replaceAll("_", "-")}`} />)}
      </div>
    </section>
  );
}

const COLS = [
  ["Asset/Contract", (t) => `${t.asset} (${t.ticker})`], ["Money", (t) => (t.env === "live" ? "REAL" : "Demo")], ["Direction", (t) => t.direction], ["Date & time (PT)", (t) => ptTime(t.opened_at)],
  ["Scan window", (t) => `${t.scan_window}s`], ["Velocity delta", (t) => `${t.velocity_delta > 0 ? "+" : ""}$${t.velocity_delta}`],
  ["Entry", (t) => `$${(t.entry_cents / 100).toFixed(2)}`], ["Exit", (t) => (t.exit_cents == null ? "—" : `$${(t.exit_cents / 100).toFixed(2)}`)],
  ["Capital", (t) => usd(t.capital_usd)], ["P/L", (t) => `${usd(t.pnl_usd)} (${pct(t.pnl_pct)})`], ["Hold", (t) => `${t.hold_secs}s`],
  ["Exit reason", (t) => t.exit_reason], ["Trigger setting", (t) => `$${t.threshold_setting}`], ["Multiplier setting", (t) => `${t.multiplier_setting}x`],
];

export function AuditLog({ trades }) {
  return (
    <section data-testid="audit-log" className="space-y-2">
      <h2 className="font-head font-bold text-base md:text-lg">Trade audit log</h2>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-subtle)]">
        <table className="w-full text-xs font-mono-num whitespace-nowrap">
          <thead className="text-[10px] uppercase tracking-widest text-[var(--text-mute)] text-left"><tr>{COLS.map(([h]) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
          <tbody>
            {(trades || []).length === 0 && <tr><td colSpan={COLS.length} className="px-2 py-3 text-[var(--text-mute)]">No closed trades yet</td></tr>}
            {(trades || []).map((t) => (
              <tr key={t.id} data-testid={`audit-row-${t.id}`} className={`border-t border-[var(--border-subtle)] ${(t.pnl_usd || 0) > 0 ? "text-[var(--buy)]" : "text-[var(--text)]"}`}>
                {COLS.map(([h, fn]) => <td key={h} className="px-2 py-1.5">{fn(t)}</td>)}
              </tr>))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function EventLog({ logs }) {
  return (
    <section data-testid="event-log" className="space-y-2">
      <h2 className="font-head font-bold text-base md:text-lg">Bot activity</h2>
      <div className="max-h-72 overflow-y-auto rounded-lg border border-[var(--border-subtle)] p-2 text-xs font-mono-num space-y-0.5">
        {(logs || []).map((l, i) => (
          <div key={i} className={l.level === "error" ? "text-red-400" : l.level === "warn" ? "text-amber-300" : "text-[var(--text-dim)]"}>
            {ptTime(l.ts)} [{l.tag}] {l.msg}
          </div>))}
      </div>
    </section>
  );
}
